import { EFontWeights } from '@appcharge/shared-ui';
import { OfferUI } from 'common/contracts';
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROMOTION_DEFAULTS,
  ROLLING_OFFER_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';

type FormValues = Record<string, unknown>;

const buildBasePayload = (formValues: FormValues, offerType: OfferDesignTypes): Partial<OfferUI> => ({
  active: true,
  offerUiType: offerType,
  name: formValues.name as string,
  externalId:
    (formValues.externalId as string)?.trim().replace(/\s+/g, '_') ||
    (formValues.name as string).trim().replace(/\s+/g, '_'),
  description: 'description',
  backgroundImage: (formValues.backgroundImage as string) || '',
  horizontalBackgroundImage: (formValues.horizontalBackgroundImage as string) || ''
});

export const buildBundlePayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    buttonSuffixImage: (formValues.buttonSuffixImage as string) || '',
    buttonSuffixAnimation: (formValues.buttonSuffixAnimation as string) || '',
    borderColor: formValues.borderColor || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth != null ? Number(formValues.borderWidth) : DEFAULT_BORDER_WIDTH
  };

  const bgColor = normalizeBgColor(formValues.backgroundColor);
  if (bgColor) {
    if (offerType === OfferDesignTypes.Bundle) {
      payload.backgroundColor = bgColor;
    } else {
      payload.specialOffer = { backgroundColor: bgColor } as OfferUI['specialOffer'];
    }
  }

  return payload as OfferUI;
};

export const buildPromotionPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    buttonSuffixImage: (formValues.buttonSuffixImage as string) || '',
    buttonSuffixAnimation: (formValues.buttonSuffixAnimation as string) || '',
    borderColor: formValues.borderColor || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth != null ? Number(formValues.borderWidth) : DEFAULT_BORDER_WIDTH,
    specialOffer: {
      templateType: PROMOTION_DEFAULTS.templateType,
      presentOfferEndTimer: false,
      title: (formValues.title as string) || '',
      fontSize: Number(formValues.fontSize) || PROMOTION_DEFAULTS.fontSize,
      fontWeight: ((formValues.fontWeight as string) || PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
      fontColor: formValues.fontColor || PROMOTION_DEFAULTS.fontColor,
      subTitle: {
        text: (formValues.subTitleText as string) || '',
        fontSize: Number(formValues.subTitleFontSize) || PROMOTION_DEFAULTS.subFontSize,
        fontWeight: (formValues.subTitleFontWeight as string) || PROMOTION_DEFAULTS.fontWeight,
        fontColor: formValues.subTitleFontColor || PROMOTION_DEFAULTS.fontColor
      },
      backgroundColor: normalizeBgColor(formValues.backgroundColor),
      collectText: (formValues.collectText as string) || PROMOTION_DEFAULTS.collectText
    } as OfferUI['specialOffer']
  };

  return payload as OfferUI;
};

export const buildProgressBarPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const barBgColor = normalizeBgColor(formValues.barBackgroundColor) || PROGRESS_BAR_DEFAULTS.backgroundColor;

  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    backgroundImage: '',
    borderColor: normalizeBgColor(formValues.borderColor) || PROGRESS_BAR_DEFAULTS.borderColor,
    borderWidth: formValues.borderWidth != null ? Number(formValues.borderWidth) : PROGRESS_BAR_DEFAULTS.borderWidth,
    backgroundColor: barBgColor,
    progressBar: {
      backgroundOpacity: Number(formValues.backgroundOpacity) ?? PROGRESS_BAR_DEFAULTS.backgroundOpacity,
      frameRadius: Number(formValues.frameRadius) ?? PROGRESS_BAR_DEFAULTS.frameRadius,
      barFillColor: normalizeBgColor(formValues.barFillColor) || PROGRESS_BAR_DEFAULTS.barFillColor,
      backgroundColor: barBgColor,
      barRadius: Number(formValues.barRadius) ?? PROGRESS_BAR_DEFAULTS.barRadius,
      accumulatedItem: {
        imageUrl: (formValues.accumulatedItemImageUrl as string) || '',
        backgroundColor: PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundColor
      },
      textColor: (formValues.textColor as string) || PROGRESS_BAR_DEFAULTS.textColor
    } as OfferUI['progressBar']
  };

  return payload as OfferUI;
};

export const buildPopupPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    ...(formValues.badgeCoverImage ? { badgeCoverImage: formValues.badgeCoverImage as string } : {}),
    borderColor: normalizeBgColor(formValues.borderColor) || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth != null ? Number(formValues.borderWidth) : DEFAULT_BORDER_WIDTH,
    specialOffer: {
      templateType: 'Single',
      presentOfferEndTimer: false,
      title: (formValues.title as string) || '',
      fontSize: Number(formValues.fontSize) || POPUP_DEFAULTS.fontSize,
      fontWeight: ((formValues.fontWeight as string) || POPUP_DEFAULTS.fontWeight) as EFontWeights,
      fontColor: formValues.fontColor || POPUP_DEFAULTS.fontColor,
      subTitle: {
        text: '',
        fontSize: POPUP_DEFAULTS.subFontSize,
        fontWeight: POPUP_DEFAULTS.subFontWeight,
        fontColor: POPUP_DEFAULTS.fontColor
      },
      backgroundColor: normalizeBgColor(formValues.backgroundColor)
    } as OfferUI['specialOffer'],
    offerUiSubType: (formValues.offerUiSubType as string) || 'DailyBonus'
  };

  return payload as OfferUI;
};

export const buildRollingOfferPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const backgroundOpacity = formValues.backgroundOpacity != null
    ? Number(formValues.backgroundOpacity)
    : ROLLING_OFFER_DEFAULTS.backgroundOpacity;
  const borderWidth = formValues.borderWidth != null
    ? Number(formValues.borderWidth)
    : ROLLING_OFFER_DEFAULTS.borderWidth;

  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    borderColor: normalizeBgColor(formValues.borderColor) || ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth,
    rollingOffer: {
      arrowColor: (formValues.arrowColor as string) || ROLLING_OFFER_DEFAULTS.arrowColor,
      arrowImage: (formValues.arrowImage as string) || '',
      headerImage: (formValues.headerImage as string) || '',
      backgroundOpacity,
      backgroundColor: normalizeBgColor(formValues.backgroundColor) || ROLLING_OFFER_DEFAULTS.backgroundColor,
      collectBehavior: 'disappear',
      enableScroll: Boolean(formValues.enableScroll),
      scrollArrowImage: (formValues.scrollArrowImage as string) || '',
      subRollingOffer: {
        backgroundColor: (() => {
          const color = normalizeBgColor(formValues.offersBackgroundColor) || ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor;
          const { gradientDirection, ...rest } = color as Record<string, unknown>;
          return { ...rest, ...(gradientDirection ? { direction: gradientDirection } : {}) };
        })(),
        ...(formValues.offersBackgroundImage ? {
          backgroundImage: formValues.offersBackgroundImage as string
        } : {}),
        lockImage: ROLLING_OFFER_DEFAULTS.lockImage,
        collectText: (formValues.collectText as string) || ROLLING_OFFER_DEFAULTS.collectText
      }
    } as OfferUI['rollingOffer']
  };

  return payload as OfferUI;
};
